import { useEffect, useState } from 'react';
import {
  useContentDispatch,
  useContentSelector,
  CollectionComponetsActions,
  ComponentsActions,
} from '@wajeez-web/stores/content';
import {
  useCommonDispatch,
  AlertType,
  CommonActions,
} from '@wajeez-web/stores/common';
import { useLocation, useNavigate } from 'react-router-dom';
import { ButtonHeadless, TextInput } from '@wajeez-web/components/ui';
import { Server } from '@wajeez/wajeez.microservices';
import { useDebounce } from '@wajeez-web/components/helpers';
import { useInfiniteScroll } from '@wajeez-web/components/page-view-builder';
import Skeleton from '@mui/material/Skeleton';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { AiOutlinePlus, AiOutlineEdit } from 'react-icons/ai';
import { BiLoaderAlt } from 'react-icons/bi';
import { Dropdown } from '@wajeez-web/components/mui';
import { DotsHorizontalIcon } from '@heroicons/react/outline';

import { Sortable, SortableItem } from '../components/Sortable/Sortable';
import { CloneModal } from '../components/CloneModal';

const PAGE_SIZE = 10;

interface DynamicZonesProps {
  collectionComponentId: string;
}

const DynamicZones = ({ collectionComponentId }: DynamicZonesProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const pathname = location.pathname;

  const contentDispatch = useContentDispatch();
  const commonDispatch = useCommonDispatch();

  const [mappedAllowedComponents, setMappedAllowedComponents] = useState<
    (Server.ComponentOutput & { isDisabled: boolean })[]
  >([]);
  const [addedComponentsOriginal, setAddedComponentsOriginal] = useState<
    { id: string | undefined; referenceName: string | undefined }[] | undefined
  >([]);
  const [addedComponents, setAddedComponents] = useState<
    { id: string | undefined; referenceName: string | undefined }[] | undefined
  >([]);
  const [allowedComponentsPageNumber, setAllowedComponentsPageNumber] =
    useState(1);
  const [allowedComponentsSearchTerm, setAllowedComponentsSearchTerm] =
    useState<string | undefined>('');
  const [shouldAddedComponentsUpdate, setShouldAddedComponentsUpdate] =
    useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string>();
  const [isCloneComponentModalOpen, setIsCloneComponentModalOpen] =
    useState(false);
  const [isCloneComponentSaveSuccess, setIsCloneComponentSaveSuccess] =
    useState(false);

  const collectionComponentDetails = useContentSelector(
    ({ collectionComponents }) => collectionComponents.details
  );
  const collectionComponentDetailsStatus = useContentSelector(
    ({ collectionComponents }) => collectionComponents.detailsStatus
  );
  const collectionComponentAddStatus = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.addComponentToCollectionComponent.status
  );
  const collectionComponentRemoveStatus = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.removeComponentFromCollectionComponent.status
  );
  const setCollectionComponentDynamicZoneStatus = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.setCollectionComponentDynamicZone.status
  );

  const allowedComponents = useContentSelector(
    ({ components }) => components.allowedComponentsForCollectionComponentType
  );
  const allowedComponentsData = allowedComponents.data;

  const cloneComponent = useContentSelector(
    ({ components }) => components.cloneComponent
  );

  const { lastElementRef } = useInfiniteScroll(
    allowedComponents.meta.hasNext,
    setAllowedComponentsPageNumber
  );

  const [setSearchTermDebounced] = useDebounce((data) => {
    contentDispatch(
      ComponentsActions.clearAllowedComponentsForCollectionComponentType()
    );

    setAllowedComponentsSearchTerm(data?.value);

    setAllowedComponentsPageNumber(1);
  });

  useEffect(() => {
    const addedComponentsDataMapped =
      collectionComponentDetails?.dynamicZone?.map((d) => {
        return {
          id: d.id,
          referenceName: d.referenceName,
        };
      });

    setAddedComponents(addedComponentsDataMapped);

    setAddedComponentsOriginal(addedComponentsDataMapped);
  }, [collectionComponentDetails]);

  useEffect(() => {
    collectionComponentId &&
      contentDispatch(
        CollectionComponetsActions.getCollectionComponentByIdAsync(
          collectionComponentId
        )
      );
  }, [collectionComponentId]);

  useEffect(() => {
    return () => {
      contentDispatch(
        ComponentsActions.clearAllowedComponentsForCollectionComponentType()
      );

      contentDispatch(
        CollectionComponetsActions.clearCollectionComponentsById()
      );
    };
  }, []);

  useEffect(() => {
    collectionComponentDetails?.type &&
      contentDispatch(
        ComponentsActions.getAllowedComponentsForCollectionComponentTypeAsync({
          pageNumber: allowedComponentsPageNumber,
          pageSize: PAGE_SIZE,
          collectionComponentTypeName: collectionComponentDetails.type,
          searchText: allowedComponentsSearchTerm,
        })
      );
  }, [
    collectionComponentDetails,
    allowedComponentsPageNumber,
    allowedComponentsSearchTerm,
  ]);

  useEffect(() => {
    setMappedAllowedComponents(
      allowedComponentsData.map((c, i) => {
        const foundAddedComponent =
          collectionComponentDetails?.dynamicZone?.find((d) => d.id === c.id);

        if (foundAddedComponent) {
          return {
            ...c,
            isDisabled: true,
          };
        } else {
          return {
            ...c,
            isDisabled: false,
          };
        }
      })
    );
  }, [allowedComponentsData, collectionComponentDetails]);

  useEffect(() => {
    let shouldUpdate = false;

    if (addedComponents) {
      for (const i in addedComponents) {
        if (addedComponents[i].id !== addedComponentsOriginal?.[i]?.id) {
          shouldUpdate = true;

          break;
        }
      }

      if (addedComponentsOriginal?.length && !addedComponents.length) {
        shouldUpdate = true;
      }

      if (
        addedComponentsOriginal &&
        addedComponents.length < addedComponentsOriginal.length
      ) {
        shouldUpdate = true;
      }
    }

    setShouldAddedComponentsUpdate(shouldUpdate);
  }, [addedComponents, addedComponentsOriginal]);

  const onItemsUpdate = (args: { successMessage?: string }) => {
    contentDispatch(CollectionComponetsActions.clearCollectionComponentsById());

    collectionComponentId &&
      contentDispatch(
        CollectionComponetsActions.getCollectionComponentByIdAsync(
          collectionComponentId
        )
      );

    contentDispatch(
      ComponentsActions.getAllowedComponentsForCollectionComponentTypeAsync({
        pageNumber: 1,
        pageSize: PAGE_SIZE,
        collectionComponentTypeName: collectionComponentDetails?.type,
        searchText: '',
      })
    );

    args.successMessage &&
      commonDispatch(
        CommonActions.setAlert({
          for: 'page',
          show: true,
          type: AlertType.SUCCESS,
          messages: [
            {
              code: 200,
              field: null,
              message: args.successMessage,
            },
          ],
        })
      );
  };

  useEffect(() => {
    if (isCloneComponentSaveSuccess && cloneComponent.data) {
      navigate(`${pathname}/components/${cloneComponent.data}/edit`);

      setIsCloneComponentSaveSuccess(false);
      setIsCloneComponentModalOpen(false);
    }
  }, [pathname, cloneComponent.data, isCloneComponentSaveSuccess, origin]);

  const renderComponentsSkeleton = () => {
    return Array.from({ length: 10 }).map((_, i) => {
      return {
        id: i.toString(),
        label: (
          <div className="h-full mt-3">
            <Skeleton
              key={i}
              sx={{ bgcolor: '#ededed' }}
              variant="rounded"
              width={'100%'}
              height={'100%'}
            />
          </div>
        ),
        isDisabled: false,
      };
    });
  };

  const renderAddedComponents = () => {
    const data = addedComponents?.map((d) => {
      return {
        id: d.id!,
        label: (
          <div key={d.id} className="w-full h-full">
            <div className="w-[70%] mx-auto h-full flex justify-center items-center text-[18px] text-center">
              {d.referenceName}
            </div>
          </div>
        ),
        actionsNode: (
          <div>
            <Dropdown
              id={`component-actions-${d.id}`}
              items={[
                {
                  label: 'Edit',
                  onClick: () => {
                    navigate(`${pathname}/components/${d.id}/edit`);
                  },
                },
                {
                  label: 'Clone',
                  onClick: () => {
                    setSelectedComponentId(d.id);
                    setIsCloneComponentModalOpen(true);
                  },
                },
              ]}
              label={
                <div style={{ color: '#343a40' }}>
                  <DotsHorizontalIcon
                    style={{ width: '28px', height: '24px' }}
                  />
                </div>
              }
              buttonStyle={{
                width: '34px',
                minWidth: '34px',
                paddingRight: '2px',
                paddingLeft: '2px',
                color: 'black',
              }}
            />
          </div>
        ),
      };
    });

    if (
      (collectionComponentDetailsStatus === 'loading' &&
        !collectionComponentDetails) ||
      collectionComponentRemoveStatus === 'loading'
    ) {
      return renderComponentsSkeleton();
    } else {
      return data;
    }
  };

  const renderComponents = () => {
    const data: SortableItem[] = mappedAllowedComponents.map((c, i) => {
      return {
        id: c.id!,
        label: (
          <div
            key={c.id}
            ref={
              i === mappedAllowedComponents.length - 1 ? lastElementRef : null
            }
            className="w-full h-full"
          >
            <div className="w-[70%] mx-auto h-full flex justify-center items-center text-[18px] text-center">
              {c.referenceName}
            </div>
          </div>
        ),
        isDisabled: c.isDisabled,
        actionsNode: (
          <div>
            <Dropdown
              id={`component-actions-${c.id}`}
              items={[
                {
                  label: 'Edit',
                  onClick: () => {
                    navigate(`${pathname}/components/${c.id}/edit`);
                  },
                },
                {
                  label: 'Clone',
                  onClick: () => {
                    setSelectedComponentId(c.id);
                    setIsCloneComponentModalOpen(true);
                  },
                },
              ]}
              label={
                <div style={{ color: '#343a40' }}>
                  <DotsHorizontalIcon
                    style={{ width: '28px', height: '24px' }}
                  />
                </div>
              }
              buttonStyle={{
                width: '34px',
                minWidth: '34px',
                paddingRight: '2px',
                paddingLeft: '2px',
                color: 'black',
              }}
            />
          </div>
        ),
      };
    });

    if (
      allowedComponents.status === 'loading' &&
      allowedComponentsData.length
    ) {
      return data.concat([
        {
          id: 'spinner',
          label: (
            <div className="py-3 w-full flex justify-center items-center">
              <BiLoaderAlt className="animate-spin text-[24px]" />
            </div>
          ),
          isDisabled: false,
          actionsNode: null,
        },
      ]);
    } else if (
      (allowedComponents.status === 'loading' &&
        !allowedComponentsData.length) ||
      collectionComponentAddStatus === 'loading'
    ) {
      return renderComponentsSkeleton();
    } else {
      return data;
    }
  };

  return (
    <>
      <div className="w-full h-full py-6 px-8">
        <div className="w-full flex justify-between items-center">
          <div className="flex justify-start items-center space-s-2">
            <div
              className="text-[19px] text-primary cursor-pointer"
              onClick={() => {
                navigate(`${pathname}/edit`);
              }}
            >
              <AiOutlineEdit />
            </div>
            <h1 className="mb-0 text-[26px] font-bold">
              {collectionComponentDetails?.referenceName}
            </h1>
          </div>
          <div className="w-[70px]">
            <ButtonHeadless
              paddingYStyle="py-1"
              action={async () => {
                const res = await contentDispatch(
                  CollectionComponetsActions.setCollectionComponentDynamicZoneAsync(
                    {
                      dynamicZoneContainerId:
                        collectionComponentDetails?.id || '',
                      componentIds: addedComponents
                        ?.map((c) => c.id)
                        .filter((id) => id) as string[],
                    }
                  )
                );

                if (res.meta.requestStatus === 'fulfilled') {
                  commonDispatch(
                    CommonActions.setAlert({
                      for: 'page',
                      show: true,
                      type: AlertType.SUCCESS,
                      messages: [
                        {
                          code: 200,
                          field: null,
                          message:
                            'The components have been updated successfully!',
                        },
                      ],
                    })
                  );

                  setAddedComponentsOriginal(addedComponents);

                  setShouldAddedComponentsUpdate(false);
                } else {
                  onItemsUpdate({});
                }
              }}
              isDisabled={!shouldAddedComponentsUpdate}
              bgStyle={
                shouldAddedComponentsUpdate ? 'bg-primary' : 'bg-[#c2c2c2]'
              }
              borderRadiusStyle="rounded"
            >
              <div className="w-full flex justify-center items-center text-white font-bold text-[16px]">
                <ReturnTranslatedText
                  origintext="Save"
                  text={`buttons.${'Save'}`}
                />
                {setCollectionComponentDynamicZoneStatus === 'loading' && (
                  <BiLoaderAlt className="mr-1 animate-spin w-5" />
                )}
              </div>
            </ButtonHeadless>
          </div>
        </div>
        <div className="mt-6">
          <Sortable
            data={[renderAddedComponents() || [], renderComponents()]}
            containerClassName="w-1/2 h-[70vh] px-8 py-6 border rounded-md overflow-y-auto overflow-x-hidden"
            containerHeaders={{
              firstContainer: (
                <h2 className="text-[20px] font-bold text-center">
                  <ReturnTranslatedText
                    origintext="Added Components"
                    text={`general.${'Added Components'}`}
                  />
                </h2>
              ),
              secondContainer: (
                <>
                  <div className="relative w-full">
                    <h2 className="text-[20px] font-bold text-center">
                      <ReturnTranslatedText
                        origintext="Components"
                        text={`general.${'Components'}`}
                      />
                    </h2>
                    <div className="absolute -top-1 left-2 w-[35px]">
                      <ButtonHeadless
                        paddingYStyle="py-[2px]"
                        action={() => {
                          navigate(`${pathname}/components/new`);
                        }}
                        borderRadiusStyle="rounded"
                        bgStyle="bg-white"
                        borderStyle="border border-primary"
                        opacityOnHover={false}
                      >
                        <div className="text-primary text-[20px]">
                          <AiOutlinePlus />
                        </div>
                      </ButtonHeadless>
                    </div>
                  </div>
                  <TextInput
                    name="component-search"
                    label="Search"
                    onChange={(name, value) => {
                      setSearchTermDebounced({ value });
                    }}
                  />
                </>
              ),
            }}
            hasExtraDeleteContainer={true}
            onItemAddToFirstContainer={async (id, order) => {
              setMappedAllowedComponents(
                mappedAllowedComponents.map((ac) => {
                  if (ac.id === id) {
                    return {
                      ...ac,
                      isDisabled: true,
                    };
                  } else {
                    return ac;
                  }
                })
              );

              const addedComponent = mappedAllowedComponents.find(
                (ac) => ac.id === id
              );

              const addedComponentsFirstSlice = addedComponents?.slice(
                0,
                order
              );
              const addedComponentsSecondSlice = addedComponents?.slice(order);

              setAddedComponents(
                addedComponentsFirstSlice?.concat(
                  {
                    id: addedComponent?.id,
                    referenceName: addedComponent?.referenceName,
                  },
                  addedComponentsSecondSlice || []
                )
              );
            }}
            onItemDeleteFromFirstContainer={async (id) => {
              setMappedAllowedComponents(
                mappedAllowedComponents.map((ac) => {
                  if (ac.id === id) {
                    return {
                      ...ac,
                      isDisabled: false,
                    };
                  } else {
                    return ac;
                  }
                })
              );

              setAddedComponents(addedComponents?.filter((c) => c.id !== id));
            }}
            onFirstContainerReorder={async (ids) => {
              setAddedComponents(
                ids.map((id) => {
                  return {
                    id,
                    referenceName: addedComponents?.find((c) => c.id === id)
                      ?.referenceName,
                  };
                })
              );
            }}
            onSecondContainerReorder={async () => {
              setMappedAllowedComponents(mappedAllowedComponents);
            }}
          />
        </div>
      </div>
      {isCloneComponentModalOpen && (
        <CloneModal
          title="Clone Component"
          isOpen={isCloneComponentModalOpen}
          onClose={() => {
            setIsCloneComponentModalOpen(false);
          }}
          onSave={async (name) => {
            const res = await contentDispatch(
              ComponentsActions.cloneComponentAsync({
                referenceName: name,
                id: selectedComponentId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setIsCloneComponentModalOpen(false);
            }
          }}
          onSaveAndEdit={async (name) => {
            const res = await contentDispatch(
              ComponentsActions.cloneComponentAsync({
                referenceName: name,
                id: selectedComponentId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setIsCloneComponentSaveSuccess(true);
            }
          }}
          isLoading={cloneComponent.status === 'loading'}
        />
      )}
    </>
  );
};

export default DynamicZones;
